import { flatMap } from 'lodash/fp';
import { createSelector } from 'reselect';
import { TextKey, RouteName } from '@mews-commander/enums';
import { getContainer } from '@mews-commander/legacy-app-instance';
import { orderOrderableEntityArray } from '@mews-commander/legacy-entities/src/utils/orderableEntities';
import { getCurrentEnterpriseActiveServices } from '@mews-commander/legacy-entities/src/selectors/services';
import { hasCurrentProfileAnyServicePrivileges } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { isAdditionalService } from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import { getSimpleSearchResult } from './getSimpleSearchResult';

export const getServicesSearchResults = createSelector(
    getContainer,
    hasCurrentProfileAnyServicePrivileges,
    getCurrentEnterpriseActiveServices,
    (container, canConfigureService, services) => {
        if (!canConfigureService || !services) {
            return [];
        }

        const orderedServices = orderOrderableEntityArray(services);

        const { appLocalizationComponent } = container();

        return flatMap((service) => {
            const { names, id } = service;
            const description = appLocalizationComponent.localizeProperty(names);
            const params = { query: { OwnerId: id } };
            const addServiceResult = ({ routeName, titleKey }: { routeName: RouteName; titleKey: Mews.TextKey }) =>
                getSimpleSearchResult({
                    id: `${routeName}${id}`,
                    routeName,
                    titleKey,
                    description,
                    params,
                });

            const basicServiceSearchResults = [
                getSimpleSearchResult({
                    id: 'detail' + id,
                    routeName: RouteName.VisitDetail,
                    titleKey: TextKey.Service,
                    description,
                    params: { serviceId: id },
                }),
                addServiceResult({
                    routeName: RouteName.ProductIndex,
                    titleKey: TextKey.ProductPlural,
                }),
                addServiceResult({
                    routeName: RouteName.BusinessSegmentIndex,
                    titleKey: TextKey.BusinessSegmentPlural,
                }),
                addServiceResult({
                    routeName: RouteName.ServiceDocumentIndex,
                    titleKey: TextKey.ServiceDocumentPlural,
                }),
            ];

            if (isAdditionalService(service)) {
                return basicServiceSearchResults;
            }

            return [
                ...basicServiceSearchResults,
                addServiceResult({
                    routeName: RouteName.ResourceFeatureIndex,
                    titleKey: TextKey.SpaceFeaturePlural,
                }),
                addServiceResult({
                    routeName: RouteName.ResourceCategoryIndex,
                    titleKey: TextKey.ResourceCategoryPlural,
                }),
                addServiceResult({
                    routeName: RouteName.ProductRuleIndex,
                    titleKey: TextKey.ProductRulePlural,
                }),
                addServiceResult({
                    routeName: RouteName.RateIndex,
                    titleKey: TextKey.RatePlural,
                }),
                addServiceResult({
                    routeName: RouteName.RateGroupIndex,
                    titleKey: TextKey.RateGroupPlural,
                }),
                addServiceResult({
                    routeName: RouteName.RestrictionIndex,
                    titleKey: TextKey.RestrictionPlural,
                }),
                getSimpleSearchResult({
                    id: 'travelAgencyContract' + id,
                    routeName: RouteName.TravelAgencyContractReportIndex,
                    titleKey: TextKey.TravelAgencyContractPlural,
                    description,
                    params: { query: { 'Service.Id': id } },
                }),
                addServiceResult({
                    routeName: RouteName.VoucherIndex,
                    titleKey: TextKey.VoucherPlural,
                }),
                addServiceResult({
                    routeName: RouteName.BookingEngineIndex,
                    titleKey: TextKey.BookingEnginePlural,
                }),
                addServiceResult({
                    routeName: RouteName.KioskIndex,
                    titleKey: TextKey.KioskPlural,
                }),
                getSimpleSearchResult({
                    id: 'registrationCardConfiguration' + id,
                    routeName: RouteName.RegistrationCardConfigurationDetail,
                    titleKey: TextKey.RegistrationCardConfiguration,
                    description,
                    params: { id },
                }),
                addServiceResult({
                    routeName: RouteName.AgeCategoryIndex,
                    titleKey: TextKey.AgeCategoryPlural,
                }),
                addServiceResult({
                    routeName: RouteName.PromotedServiceAssignmentIndex,
                    titleKey: TextKey.PromotedServiceAssignmentPlural,
                }),
            ];
        }, orderedServices);
    }
);
